import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useVehicleSelection } from '../hooks/useVehicleSelection'
import useCardsFetcher from '../hooks/useCardsFetcher'
import CardDto from '../interfaces/CardDto'
import { useAuth } from '../hooks/useAuth'
import useFilteredData from '../hooks/useFilteredData'
import { AppPath } from '../enums/AppPath'
import filterFromTo from '../utils/filterFromTo'
import UserFilter from '../modules/users/components/UserFilter'
import filterContains from '../utils/filterContains'
import { ConsignmentState, VehicleState } from '../enums/DocStates'
import { PageState } from '../interfaces/PageState'
import { DocumentsFilter, VehicleFilterForm } from '../interfaces/CardOne'
import { VehicleSort } from '../modules/vehicles/components/sort-bar/types'
import { translateBody, getDisplayValue } from '../utils/vehicleAttributeTranslate'
import { BODY_TYPE_MAPPINGS } from '../utils/strictValidationMappings'

import VehicleFilter from './VehicleFilter'
import VehicleSelectionToolbar from './VehicleSelectionToolbar'
import CardOneSearch from './CardOneSearch'
import CardOneSet from './CardOneSet'
import CardOneBtn from './CardOneBtn'

import { Flex, Select, Text } from '@radix-ui/themes'
import { VehiclesSortBar } from '@/modules/vehicles/components/sort-bar/VehiclesSortBar'
import { ScreenWrapper } from '@/modules/common/components/ScreenWrapper'
import { validateAndIdentifyRut } from '../utils/rutValidation'

const filterDataIni = {
  yearFrom_format: '',
  yearTo_format: '',
  priceFrom_format: '',
  priceTo_format: '',
  kmFrom_format: '',
  kmTo_format: '',
  branch: '',
  gearbox: '',
  fuel: '',
  traction: '',
  body: '',
}

const documentsFilterDefault = {
  salesPerson: '',
  dateSince: '',
  dateUntil: '',
}

interface CardOnePageProps {
  docuType: AppPath
  showButtons?: boolean
  // Indicates if the vehicle we are calling it to "create"
  // From another document
  select?: number
  onClickCard?: (id: string) => void
  showCreaButton?: boolean
  // When we want the click on the "Create" button to do something else
  onCreateClick?: () => void
  searchTermIni?: string
  // New prop to enable bulk publishing
  enableBulkPublishing?: boolean
  onBulkPublish?: (vehicleIds: string[]) => void
}

/**
 * It shows the main page of the project, with a search bar, a button (optional).
 */
const CardOnePage: React.FC<CardOnePageProps> = ({
  docuType,
  showButtons = true,
  select = 0,
  onClickCard,
  showCreaButton,
  onCreateClick,
  searchTermIni = '',
  enableBulkPublishing = false,
  onBulkPublish,
}) => {
  //  The amount of cards per page
  const [pageSize, setPageSize] = useState<number>(8)

  const [array, setArray] = useState<CardDto[]>([])
  const { error, loading, fetchData } = useCardsFetcher()
  const [estado, setEstado] = useState<number>(0)
  const [searchTerm, setSearchTerm] = useState<string>(searchTermIni)
  const { setVehicleForDoc } = useAuth()
  const [filterData, setFilterData] = useState<VehicleFilterForm | null>(filterDataIni)
  const [documentsFilter, setDocumentsFilter] = useState<DocumentsFilter>(documentsFilterDefault)
  const esVehiculo = docuType === AppPath.Vehicles
  const esCotizacion = docuType === AppPath.Estimates
  const esConsignacion = docuType === AppPath.Consignments
  const esNotaVenta = docuType === AppPath.SaleNotes
  const hayFiltroUsuario = esCotizacion || esConsignacion || esNotaVenta
  // So that it does not start filtering when the cards are still loading
  const [allowFilter, setAllowFilter] = useState<boolean>(false)
  // The click button
  const [btnClicked, setBtnClicked] = useState<number>(0)
  // The selected page
  const [pageCurrent, setPageCurrent] = useState<number>(1)
  const [sort, setSort] = useState<VehicleSort>({
    field: 'createdAt',
    order: 'desc',
  })
  const [clientFilter, setClientFilter] = useState<string>('all')

  // Vehicle selection hook
  const vehicleSelection = useVehicleSelection({
    vehicles: array,
    onBulkPublish,
  })

  // For search
  let filtered = useFilteredData({
    array: array,
    filterFields: [
      (item) => item.vehicle?.plate ?? '',
      (item) => item.vehicle?.brand ?? '',
      (item) => item.vehicle?.model ?? '',
      (item) => (item.number ?? '').toString(),
      (item) => item.client.email,
      (item) => item.client.lastname,
      (item) => item.client.firstname,
      // so that the user can typing model brand
      (item) => `${item.vehicle?.brand} ${item.vehicle?.model}`,
      (item) => `${item.client.firstname} ${item.client.lastname}`,
    ],
    searchTerm,
    allowFilter,
  })

  if (esVehiculo && filterData && allowFilter) {
    // Without debouncing, for now
    filtered = filterFromTo({
      array: filtered,
      filterField: (item) => item.vehicle.year,
      searchTermFrom: filterData?.yearFrom_format ? (filterData.yearFrom ?? null) : null,
      searchTermTo: filterData?.yearTo_format ? (filterData.yearTo ?? null) : null,
    })
    filtered = filterFromTo({
      array: filtered,
      filterField: (item) => item.vehicle.price,
      searchTermFrom: filterData?.priceFrom_format ? (filterData.priceFrom ?? null) : null,
      searchTermTo: filterData?.priceTo_format ? (filterData.priceTo ?? null) : null,
    })
    filtered = filterFromTo({
      array: filtered,
      filterField: (item) => item.vehicle?.odometer ?? 0,
      searchTermFrom: filterData?.kmFrom_format ? (filterData.kmFrom ?? null) : null,
      searchTermTo: filterData?.kmTo_format ? (filterData.kmTo ?? null) : null,
    })

    if ((filterData.branch ?? '').length > 0) {
      filtered = filtered.filter((card) => card.vehicle.branch === filterData.branch)
    }

    if ((filterData.fuel ?? '').length > 0) {
      filtered = filtered.filter((card) => card.vehicle.fuel === filterData.fuel)
    }

    if ((filterData.gearbox ?? '').length > 0) {
      filtered = filtered.filter((card) => card.vehicle.gearbox === filterData.gearbox)
    }

    if ((filterData.traction ?? '').length > 0) {
      const awd = 'CHARACTERISTICS.VEHICLE.COMFORT.ALL_WHEEL_DRIVE'
      filtered =
        filterData.traction === 'awd'
          ? filtered.filter((card) => card.vehicle.characteristics?.includes(awd))
          : filtered.filter((card) => !card.vehicle.characteristics?.includes(awd))
    }

    if ((filterData.body ?? '').length > 0) {
      filtered = filtered.filter((card) => {
        // Get the display value for the vehicle's body type using the same logic as the reports
        const vehicleBodyDisplay = getDisplayValue(card.vehicle.body_format, card.vehicle.body, translateBody)
        const selectedBody = filterData.body

        // Direct match first (handles most cases including new body types)
        if (vehicleBodyDisplay === selectedBody) {
          return true
        }

        // For backward compatibility, check platform mappings for common body types
        // This handles cases where the selected body type has platform-specific variations
        const mapping = BODY_TYPE_MAPPINGS.find((m) => m.common === selectedBody)

        if (mapping) {
          return (
            vehicleBodyDisplay === mapping.chileautos ||
            vehicleBodyDisplay === mapping.mercadolibre ||
            vehicleBodyDisplay === mapping.yapo
          )
        }

        // Additional fallback: handle cases where formatted/raw values might differ
        // This ensures we don't miss matches due to formatting differences
        if (card.vehicle.body_format && card.vehicle.body_format === selectedBody) {
          return true
        }

        return false
      })
    }
  }

  if (hayFiltroUsuario && documentsFilter) {
    if (documentsFilter.salesPerson) {
      filtered = filterContains({
        list: filtered,
        filterField: (item) => `${item.user?.firstname ?? ''} ${item.user?.lastname ?? ''}`,
        searchTerm: documentsFilter.salesPerson ?? '',
      })
    }

    if (documentsFilter.dateSince) {
      filtered = filterContains({
        list: filtered,
        filterField: (item) => item.date || item.createdAt,
        dateSince: documentsFilter.dateSince,
      })
    }

    if (documentsFilter.dateUntil) {
      filtered = filterContains({
        list: filtered,
        filterField: (item) => item.date || item.createdAt,
        dateUntil: documentsFilter.dateUntil,
      })
    }
  }

  if (docuType === AppPath.Clients) {
    const getClientType = (c: CardDto) => {
      if (c.client.type) return c.client.type;
      if (c.client.nin) return validateAndIdentifyRut(c.client.nin).type;
      return 'Natural Person'; // Fallback
    };

    if (clientFilter === 'natural') {
      filtered = filtered.filter((c) => getClientType(c) !== 'Legal Entity');
    } else if (clientFilter === 'company') {
      filtered = filtered.filter((c) => getClientType(c) === 'Legal Entity');
    }
  }

  if (sort?.field && sort?.order) {
    filtered = filtered.sort((a, b) => {
      const aValue = sort.field === 'createdAt' ? a[sort.field] : a.vehicle[sort.field]
      const bValue = sort.field === 'createdAt' ? b[sort.field] : b.vehicle[sort.field]

      if (sort.field === 'createdAt') {
        return sort.order === 'asc'
          ? new Date(aValue).getTime() - new Date(bValue).getTime()
          : new Date(bValue).getTime() - new Date(aValue).getTime()
      }

      if (sort.order === 'asc') {
        return aValue < bValue ? -1 : 1
      } else {
        return aValue > bValue ? -1 : 1
      }
    })
  }

  // select> 0 yes and only if we are looking for a vehicle from
  // The "Create" button of some document (quotation, etc)
  useEffect(() => {
    setVehicleForDoc(select)
  }, [select, setVehicleForDoc])

  const location = useLocation()

  // If you come from another screen (for example, from returning)
  // Can come from the window to create consignments -> select vehicles
  //  And in this case the docutype is the same, but the select no
  useEffect(() => {
    //All: the others
    let estado = 0

    if (esVehiculo) {
      // If it comes from another screen (with Pagestate, then we use the state that comes)
      // If not, that is, it comes from the menu, then we show the first option

      const params = new URLSearchParams(location.search)

      if (params.get('pageState')) {
        const pageState: PageState = JSON.parse(decodeURIComponent(params.get('pageState')!))
        estado = pageState?.state ?? 0

        setSearchTerm(pageState.searchTerm)
        setPageCurrent(pageState.pageCurrent)
        setFilterData(pageState?.vehicleFilterForm)
      }
    }
    //It doesn't matter what state is a "rare" state, because in those cases the button does not appear
    doButtonClick(estado)
    setPageSize(docuType === AppPath.Clients ? 16 : 8)
  }, [docuType, select])

  // Click on a Btncard button
  const handleButtonClick = async (index: number) => {
    await doButtonClick(index)
    // If we change state (published, slopes, etc)
    // you have to reset the page
    setPageCurrent(1)
    // Reset selection when changing vehicle state
    vehicleSelection.resetSelection()
  }
  const doButtonClick = async (index: number) => {
    setBtnClicked(index)

    if (select > 0) {
      index = select
    }
    const data = await fetchData(docuType, index)
    setArray(data)
    setEstado(index)
    setAllowFilter(true)
  }

  let headerText: string = 'Gestionar '

  switch (docuType) {
    case AppPath.Consignments:
      if (select === ConsignmentState.Liquidables) {
        headerText = 'Buscar Consignaciones para liquidar'
        showCreaButton = false
      } else {
        headerText = 'Gestionar Consignaciones'
      }
      break
    case AppPath.Vehicles:
      headerText = 'Gestionar Vehículos'

      if (select === VehicleState.Consignables) {
        headerText += ' para consignación'
      } else if (select === VehicleState.Cotizables) {
        headerText += ' para cotización'
      } else if (select === VehicleState.NotasVendibles) {
        headerText += ' para nota de venta'
      } else if (select === VehicleState.Reservables) {
        headerText += ' para reserva'
      } else if (select === VehicleState.Comprables) {
        headerText += ' para compra'
      } else if (select === VehicleState.Creditables) {
        headerText = 'Buscar Vehículos para gestionar crédito'
        showCreaButton = false
      }
      break
    case AppPath.Clients:
      headerText = 'Gestionar Clientes'

      if (select === VehicleState.Creditables) {
        headerText = 'Buscar Clientes para gestionar crédito'
        showCreaButton = false
      }
  }

  const onClearFilter = () => {
    setDocumentsFilter(documentsFilterDefault)
    setFilterData(filterDataIni)
  }
  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(filtered.length / pageSize)

    if (newPage > 0 && newPage <= totalPages) {
      setPageCurrent(newPage)
    }
  }

  // In addition to maintaining the state, you have to move the page
  // to the first. If not, it is blank
  const setSearchTermEx = (term: string) => {
    setSearchTerm(term)
    setPageCurrent(1)
  }

  const pageState: PageState = {
    state: estado,
    pageCurrent: pageCurrent,
    url: location.pathname,
    searchTerm: searchTerm,
    pageSize: pageSize,
    vehicleFilterForm: filterData,
  }

  // Show bulk publishing toolbar only for vehicles and when enabled
  const showBulkPublishing = enableBulkPublishing && esVehiculo && filtered.length > 0

  return (
    <ScreenWrapper>
      <CardOneSearch
        headerText={headerText}
        docuType={docuType}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTermEx}
        showCreaButton={showCreaButton}
        onCreateClick={onCreateClick}
        onClearClick={onClearFilter}
        pageState={pageState}
      />
      {esVehiculo && !!filterData && (
        <div className="mt-4 mb-4">
          <VehicleFilter formData={filterData} setFormData={setFilterData} />
        </div>
      )}
      {hayFiltroUsuario && (
        <div className="mt-4 mb-4">
          <UserFilter setFilterData={setDocumentsFilter} filterData={documentsFilter} />
        </div>
      )}
      {showButtons && (
        <CardOneBtn docuType={docuType} handleButtonClick={handleButtonClick} clickedIndex={btnClicked} />
      )}
      {esVehiculo && (
        // Vehicle sorting fields
        <VehiclesSortBar sort={sort} setSort={setSort} />
      )}

      {showBulkPublishing && (
        <VehicleSelectionToolbar
          isSelectionMode={vehicleSelection.isSelectionMode}
          selectedCount={vehicleSelection.selectedCount}
          totalCount={vehicleSelection.totalCount}
          onToggleSelectionMode={vehicleSelection.toggleSelectionMode}
          onSelectAll={vehicleSelection.selectAll}
          onSelectNone={vehicleSelection.selectNone}
          onBulkPublish={vehicleSelection.handleBulkPublish}
          canPublish={vehicleSelection.canPublish}
        />
      )}

      {docuType === AppPath.Clients && (
        <Flex direction="row" gap="4" align="center" mb="4">
          <Text size="2" weight="bold">
            Filtrar por tipo:
          </Text>
          <Select.Root value={clientFilter} onValueChange={setClientFilter}>
            <Select.Trigger />
            <Select.Content>
              <Select.Item value="all">Todos</Select.Item>
              <Select.Item value="natural">Personas Naturales</Select.Item>
              <Select.Item value="company">Empresas</Select.Item>
            </Select.Content>
          </Select.Root>
        </Flex>
      )}

      <CardOneSet
        cardsData={filtered}
        docuType={docuType}
        estado={estado}
        onClickCard={onClickCard}
        pageState={pageState}
        handlePageChange={handlePageChange}
        isSelectionMode={vehicleSelection.isSelectionMode}
        selectedVehicleIds={vehicleSelection.selectedVehicleIds}
        onSelectionChange={vehicleSelection.handleSelectionChange}
      />
      {loading && <div>Loading...</div>}
      {error && <div>Error: {error.message}</div>}
    </ScreenWrapper>
  )
}

export default CardOnePage
